import React, { useEffect, useRef, useState } from "react";
import userService from "../../services/UserService";
import { GetUserDto, UpdateUserDto } from "../../shared/dtos/Usuario.dto";
import loadSCGif from "../../assets/load_SC.gif";
import Swal from "sweetalert2";
import CadastroUsuarioModal from "../../components/CadastroUsuarioModal/cadastroUsuarioModal";
import { FaRegUserCircle } from "react-icons/fa";
import Button from "../../components/Button/button";
import {
  ButtonAction,
  MenuTable,
  MenuTableDivider,
  MenuTableItem,
  Table,
  TableContainer,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/Table/styles";
import { IoMdClose } from "react-icons/io";
import { SlOptionsVertical } from "react-icons/sl";
import { LuEye, LuEyeOff } from "react-icons/lu";
import { Colors } from "../../shared/colors/colors";
import {
  BlueTitle,
  Column,
  GrayTitle,
  ModalBody,
  ModalBodyContainer,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  StyledHR,
} from "../../shared/styles/global.styles";
import {
  FormContainer,
  InputContainer,
  Label,
  LoadingGif,
  LoadingOverlay,
  MainContainer,
  ModalButtons,
  PasswordContainer,
  PasswordToggleButton,
  RowContainer,
  StatusCell,
  StyledInput,
  Switch,
  SwitchInput,
  SwitchRow,
  SwitchSpan,
  TitleAndButton,
} from "./userList.styles";
import { PermissoesDto } from "../../shared/dtos/Permissoes.dto";
import { CreateControleAcessoDto } from "../../shared/dtos/ControleAcesso.dto";
import modulosService from "../../services/ModulosService";
import { ModulosDto } from "../../shared/dtos/Modulos.dto";
import permissoesService from "../../services/PermissoesService";
import { IoCloseSharp } from "react-icons/io5";
import { useAuth } from "../../AuthContext/AuthContext";
import { ModulosEnum } from "../../shared/enum/modulos.enum";
import { PermissoesEnum } from "../../shared/enum/permicao.enum";

const UsersList: React.FC = () => {
  const menuRefsMap = useRef<Map<number, HTMLDivElement | null>>(new Map());
  const { checkPermission } = useAuth();
  const editOnly = checkPermission(
    ModulosEnum.USUARIOS,
    PermissoesEnum.ESCRITA
  );
  // Estados
  const [users, setUsers] = useState<GetUserDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showCadastroModal, setShowCadastroModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<GetUserDto | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [modulos, setModulos] = useState<ModulosDto[]>();
  const [permissoes, setPermissoes] = useState<PermissoesDto[]>();
  const [formData, setFormData] = useState<UpdateUserDto>({
    name: "",
    email: "",
    isActive: false,
    password: "",
  });
  const [controleAcesso, setControleAcesso] = useState<
    CreateControleAcessoDto[]
  >([]);
  const [isOpen, setIsOpen] = useState<number | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const menuRef = useRef<HTMLDivElement>(null);

  // Efeito para fechar o dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Alternar exibição do dropdown
  const toggleDropdown = (id: number) => {
    setIsOpen(id === isOpen ? null : id);
  };

  // Lidar com ações do dropdown
  const actionsDropdown = (action: string, userId: number) => {
    if (action === "edit") {
      const user = users.find((u) => u.id === userId);
      if (user) {
        openEditModal(user);
      }
    } else if (action === "delete") {
      handleDeleteUser(userId);
    }
    setIsOpen(null);
  };

  // Buscar usuários
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getAllUsers();
      setUsers(data);
    } catch (error) {
      setError("Erro ao carregar os usuários");
      console.error("Erro ao buscar usuários:", error);
      Swal.fire(
        "Erro",
        "Erro ao carregar os usuários. Tente novamente mais tarde.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Buscar modulos
  const fetchModulos = async () => {
    try {
      setLoading(true);
      const data = await modulosService.getAllModulos();
      setModulos(data);
    } catch (error) {
      setError("Erro ao carregar os modulos");
      console.error("Erro ao buscar modulos:", error);
      Swal.fire(
        "Erro",
        "Erro ao carregar os modulos. Tente novamente mais tarde.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Buscar permissões
  const fetchPermissoes = async () => {
    try {
      setLoading(true);
      const data = await permissoesService.getAllPermissoes();
      setPermissoes(data);
    } catch (error) {
      setError("Erro ao carregar os modulos");
      console.error("Erro ao buscar modulos:", error);
      Swal.fire(
        "Erro",
        "Erro ao carregar os modulos. Tente novamente mais tarde.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal de edição
  const openEditModal = (user: GetUserDto) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      // permission: user.permission,
      isActive: user.isActive,
      password: "",
    });

    setControleAcesso(user.controleAcesso || []);
    setShowModal(true);
  };

  // Manipuladores de formulário
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, isActive: e.target.checked });
  };

  // Atualizar usuário
  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    setIsSaving(true);

    try {
      // Cria um objeto apenas com os campos que foram modificados
      const updateData: Partial<UpdateUserDto> = {};

      if (formData.name !== selectedUser.name) {
        updateData.name = formData.name;
      }

      if (formData.email !== selectedUser.email) {
        updateData.email = formData.email;
      }

      // if (formData.permission !== selectedUser.permission) {
      //   updateData.permission = formData.permission;
      // }

      if (formData.isActive !== selectedUser.isActive) {
        updateData.isActive = formData.isActive;
      }

      // Só inclui a senha se foi fornecida uma nova
      if (formData.password && formData.password.trim() !== "") {
        updateData.password = formData.password;
      }

      await userService.updateUser(selectedUser.id, updateData, controleAcesso);

      setShowModal(false);

      // Atualiza o usuário na lista local
      // setUsers((prevUsers) =>
      //   prevUsers.map((user) =>
      //     user.id === selectedUser.id ? { ...user, ...updateData } : user
      //   )
      // );
      await fetchUsers();

      Swal.fire({
        icon: "success",
        title: "Sucesso",
        text: "Usuário atualizado com sucesso!",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      });
    } catch (error) {
      console.error("Erro ao atualizar o usuário:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Ocorreu um erro ao atualizar o usuário.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Excluir usuário
  const handleDeleteUser = async (userId: number) => {
    const result = await Swal.fire({
      title: "Tem certeza?",
      text: "Esta ação não pode ser desfeita!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sim, excluir!",
      cancelButtonText: "Cancelar",
      allowOutsideClick: false,
    });

    if (result.isConfirmed) {
      setIsSaving(true);

      try {
        await userService.softDeleteUser(userId);

        // Remove o usuário da lista local
        setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId));

        Swal.fire({
          icon: "success",
          title: "Excluído!",
          text: "O usuário foi excluído com sucesso.",
          confirmButtonText: "OK",
          allowOutsideClick: false,
        });
      } catch (error) {
        console.error("Erro ao excluir usuário:", error);
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: "Ocorreu um erro ao excluir o usuário.",
          confirmButtonText: "OK",
          allowOutsideClick: false,
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  // Buscar usuários ao montar o componente
  useEffect(() => {
    fetchUsers();
    fetchModulos();
    fetchPermissoes();
  }, []);

  // Renderização do componente de carregamento
  if (loading && !isSaving) {
    return (
      <LoadingOverlay>
        <LoadingGif src={loadSCGif} alt="Carregando..." />
      </LoadingOverlay>
    );
  }

  // Renderização de erro
  if (error) {
    return <div>{error}</div>;
  }

  return (
    <MainContainer>
      {/* Overlay de carregamento durante operações de salvamento */}
      {isSaving && (
        <LoadingOverlay>
          <LoadingGif src={loadSCGif} alt="Carregando..." />
        </LoadingOverlay>
      )}

      {/* Cabeçalho com título e botão de cadastro */}
      <TitleAndButton>
        <GrayTitle>Usuários</GrayTitle>
        {editOnly && (
          <Button
            schemaButton="full-primary"
            action={() => setShowCadastroModal(true)}
            icon={
              <FaRegUserCircle
                style={{ fontSize: "1rem", marginRight: "0.3rem" }}
              />
            }
          >
            Cadastrar novo usuário
          </Button>
        )}
      </TitleAndButton>
      <StyledHR />
      {/* Tabela de usuários */}
      <TableContainer>
        <Table id="users-table">
          <TableHead>
            <TableRow>
              <TableHeader>Nome</TableHeader>
              <TableHeader>E-mail</TableHeader>
              {/* <TableHeader>Permissão</TableHeader> */}
              <TableHeader>Ativo</TableHeader>
              {editOnly && <TableHeader>Ações</TableHeader>}
            </TableRow>
          </TableHead>
          <tbody>
            {users.length > 0 ? (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableData>{user.name}</TableData>
                  <TableData>{user.email}</TableData>
                  {/* <TableData>{getUserTypeLabel(user.permission)}</TableData> */}
                  <StatusCell status={user.isActive ? "ativo" : "inativo"}>
                    {user.isActive ? "Ativo" : "Inativo"}
                  </StatusCell>
                  {editOnly && (
                    <TableData
                      style={{
                        position: "relative",
                        overflow: "visible",
                      }}
                      key={`actions-${user.id}`}
                    >
                      <ButtonAction
                        onClick={() => toggleDropdown(user.id)}
                        style={{ marginLeft: "auto" }}
                      >
                        {isOpen === user.id ? (
                          <IoMdClose
                            cursor={"pointer"}
                            size={20}
                            style={{ color: Colors.primary }}
                          />
                        ) : (
                          <SlOptionsVertical
                            cursor={"pointer"}
                            size={20}
                            style={{ color: Colors.primary }}
                          />
                        )}
                      </ButtonAction>
                      <MenuTable
                        show={isOpen === user.id}
                        align="start"
                        ref={(el: HTMLDivElement | null) => {
                          // Armazena a referência no mapa usando o ID do registro como chave
                          menuRefsMap.current.set(user.id, el);
                        }}
                        isLastRows={
                          users.length > 0 &&
                          users.indexOf(user) >=
                            users.length - Math.ceil(users.length / 2)
                        }
                      >
                        <MenuTableItem
                          eventKey="1"
                          onClick={() => actionsDropdown("edit", user.id)}
                        >
                          Editar
                        </MenuTableItem>
                        <MenuTableDivider />
                        <MenuTableItem
                          eventKey="2"
                          onClick={() => actionsDropdown("delete", user.id)}
                        >
                          Excluir
                        </MenuTableItem>
                      </MenuTable>
                    </TableData>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableData colSpan={5} style={{ textAlign: "center" }}>
                  Nenhum usuário encontrado.
                </TableData>
              </TableRow>
            )}
          </tbody>
        </Table>
      </TableContainer>

      {/* Modal de Cadastro */}
      {showCadastroModal && (
        <CadastroUsuarioModal
          onClose={() => setShowCadastroModal(false)}
          onSuccess={() => fetchUsers()}
          showModal={showCadastroModal}
          setShowModal={setShowCadastroModal}
        />
      )}

      {/* Modal de Edição */}
      {showModal && selectedUser && (
        <ModalContainer show={showModal} >
          <ModalContent width={50}>
            <Button
              type="button"
              schemaButton="x-close"
              action={() => setShowModal(false)}
              icon={<IoCloseSharp size={30} />}
              position="absolute"
              top={10}
              right={10}
            />
            <ModalHeader>
              <BlueTitle>Editar Usuário</BlueTitle>
            </ModalHeader>

            <ModalBody>
              <Column>
                <RowContainer>
                  <InputContainer>
                    <Label>Nome</Label>
                    <StyledInput
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      disabled={!formData.isActive}
                    />
                  </InputContainer>

                  <InputContainer>
                    <Label>Email</Label>
                    <StyledInput
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={!formData.isActive}
                    />
                  </InputContainer>
                </RowContainer>
                {/* <InputContainer>
                  <Label>Permissão</Label>
                  <StyledSelect
                    name="permission"
                    value={formData.permission}
                    onChange={handleSelectChange}
                    disabled={!formData.isActive}
                  >
                    <option value={UsersEnumType.ADMIN}>Administrador</option>
                    <option value={UsersEnumType.CASH}>Caixa</option>
                    <option value={UsersEnumType.USER}>Usuário</option>
                  </StyledSelect>
                </InputContainer> */}
                <RowContainer>
                  <InputContainer>
                    <Label>Nova Senha (opcional)</Label>
                    <PasswordContainer>
                      <StyledInput
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password || ""}
                        onChange={handleInputChange}
                        placeholder="Digite a nova senha"
                        disabled={!formData.isActive}
                      />
                      <PasswordToggleButton
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={!formData.isActive}
                      >
                        {showPassword ? (
                          <LuEyeOff
                            size={20}
                            color={formData.isActive ? "#666" : "#999"}
                          />
                        ) : (
                          <LuEye
                            size={20}
                            color={formData.isActive ? "#666" : "#999"}
                          />
                        )}
                      </PasswordToggleButton>
                    </PasswordContainer>
                  </InputContainer>

                  <SwitchRow>
                    <Switch>
                      <SwitchInput
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={handleCheckboxChange}
                      />
                      <SwitchSpan />
                    </Switch>
                    <span>
                      {formData.isActive ? "Usuário Ativo" : "Usuário Inativo"}
                    </span>
                  </SwitchRow>
                </RowContainer>
                <BlueTitle fontSize={18}>Permissões de acesso:</BlueTitle>
                <TableContainer>
                  <Table id="permissoes-table">
                    <TableHead>
                      <TableRow>
                        <TableHeader style={{ width: "150px" }}>
                          Tela
                        </TableHeader>
                        <TableHeader style={{ width: "100px" }}>
                          Permissões
                        </TableHeader>
                      </TableRow>
                    </TableHead>
                    <tbody>
                      {modulos && modulos.length > 0 ? (
                        modulos.map((modulo) => (
                          <TableRow key={modulo.id}>
                            <TableData>{modulo.nome}</TableData>

                            <TableData
                              style={{
                                whiteSpace: "nowrap",
                              }}
                            >
                              {permissoes?.map((permissao) => (
                                <div
                                  key={permissao.id}
                                  style={{
                                    display: "inline-block",
                                    marginRight: "1rem",
                                    verticalAlign: "middle",
                                  }}
                                >
                                  <Switch>
                                    <SwitchInput
                                      type="checkbox"
                                      id={`permissao-${modulo.id}-${permissao.id}`}
                                      checked={
                                        !formData.isActive
                                          ? false
                                          : controleAcesso
                                          ? controleAcesso.some(
                                              (controle) =>
                                                controle.idModulos ===
                                                  modulo.id &&
                                                controle.idPermissoes ===
                                                  permissao.id
                                            )
                                          : false
                                      }
                                      onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        const controleAcessoAtual =
                                          controleAcesso ?? [];

                                        const updatedControleAcesso = isChecked
                                          ? permissao.id === 1
                                            ? [
                                                ...controleAcessoAtual,
                                                {
                                                  idModulos: modulo.id,
                                                  idPermissoes: 1,
                                                },
                                                {
                                                  idModulos: modulo.id,
                                                  idPermissoes: 2,
                                                },
                                              ]
                                            : [
                                                ...controleAcessoAtual,
                                                {
                                                  idModulos: modulo.id,
                                                  idPermissoes: permissao.id,
                                                },
                                              ]
                                          : controleAcessoAtual.filter(
                                              (controle) => {
                                                // If trying to uncheck permission 2 and permission 1 is checked, keep permission 2
                                                if (
                                                  permissao.id === 2 &&
                                                  controleAcessoAtual.some(
                                                    (c) =>
                                                      c.idModulos ===
                                                        modulo.id &&
                                                      c.idPermissoes === 1
                                                  ) &&
                                                  controle.idModulos ===
                                                    modulo.id &&
                                                  controle.idPermissoes === 2
                                                ) {
                                                  return true;
                                                }

                                                return !(
                                                  controle.idModulos ===
                                                    modulo.id &&
                                                  controle.idPermissoes ===
                                                    permissao.id
                                                );
                                              }
                                            );

                                        setControleAcesso(
                                          updatedControleAcesso
                                        );
                                      }}
                                      disabled={!formData.isActive}
                                    />

                                    <SwitchSpan />
                                  </Switch>
                                  <label
                                    htmlFor={`permissao-${modulo.id}-${permissao.id}`}
                                    style={{ marginLeft: "5px" }}
                                  >
                                    {permissao.role}
                                  </label>
                                </div>
                              ))}
                            </TableData>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableData
                            colSpan={2}
                            style={{ textAlign: "center" }}
                          >
                            Nenhum tela encontrada.
                          </TableData>
                        </TableRow>
                      )}
                    </tbody>
                  </Table>
                </TableContainer>
              </Column>
            </ModalBody>
            <ModalFooter>
              <Button
                type="button"
                schemaButton="out-danger"
                action={() => setShowModal(false)}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                schemaButton="full-primary"
                action={handleUpdateUser}
              >
                Salvar
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalContainer>
      )}
    </MainContainer>
  );
};

export default UsersList;
